// src/components/Layout/Navbar.tsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
// import { useData } from '../../contexts/DataContext';
import { useMapState } from '../../hooks/useMapState';
import { DataRecord } from '../../types/DataTypes';
import { ExportConfiguration, ShareConfiguration } from '../../types/ViewTypes';
import {
  exportData,
  generateShareUrl,
  shareToPlatform,
  SocialShareOptions
} from '../../utils/exportUtils';

interface CustomDataFilters {
  years?: number[];
  regions?: string[];
  countries?: string[];
  variables?: string[];
}

const Navbar: React.FC = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isForecastDropdownOpen, setIsForecastDropdownOpen] = useState(false);
  
  // Data context for accessing all data - temporarily disabled
  // const { 
  //   data, 
  //   filteredData, 
  //   variables, 
  //   regions, 
  //   years,
  //   currentFilters,
  //   setFilters 
  // } = useData();
  
  // Fallback values while DataContext is disabled
  const data: any[] = [];
  const filteredData: any[] = [];
  const variables: any[] = [];
  const regions: any[] = [];
  const years: number[] = [];
  const currentFilters = {};
  const setFilters = () => {};
  
  const { selectedVariable, selectedYear } = useMapState();
  
  // Export and share modal states
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  
  // Export configuration
  const [exportConfig, setExportConfig] = useState<ExportConfiguration>({
    format: 'csv',
    fileName: 'regional-data-export',
    width: 1920,
    height: 1080,
    dpi: 300,
    includeFilters: true,
    includeLegend: true,
    includeTitle: true,
    includeFooter: true
  });
  
  // Share configuration
  const [shareConfig, setShareConfig] = useState<ShareConfiguration>({
    includeFilters: true,
    includeViewConfiguration: true,
    generateShortLink: true,
    expiresAfter: 30 // 30 days
  });
  
  // State for full dataset export option
  const [exportFullDataset, setExportFullDataset] = useState(false);

  // Selected variables for export
  const [selectedExportVariables, setSelectedExportVariables] = useState<string[]>([]);
  
  // Share URL state
  const [shareUrl, setShareUrl] = useState<string>('');
  const [shareUrlCopied, setShareUrlCopied] = useState(false);
  
  // Social share options
  const [socialShareOptions, setSocialShareOptions] = useState<SocialShareOptions>({
    url: '',
    title: 'Regional Data Explorer Analysis',
    description: 'Check out this analysis from the Regional Data Explorer',
    hashtags: ['RegionalData', 'DataAnalysis', 'Visualization']
  });
  
  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  // Check if the current path matches the given path
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  // Handle export data
  const handleExportData = () => {
    // Determine which dataset to export
    const dataToExport = exportFullDataset ? data : filteredData;

    // Get variables to export, or all if none selected
    const variablesToExport = selectedExportVariables.length > 0
      ? selectedExportVariables
      : variables.map(v => v.id);

    // Export the data
    exportData(dataToExport, exportConfig, variablesToExport);

    // Close the modal
    setExportModalOpen(false);
  };
  
  // Handle variable selection
  const handleVariableToggle = (variableId: string) => {
    if (selectedExportVariables.includes(variableId)) {
      setSelectedExportVariables(
        selectedExportVariables.filter(id => id !== variableId)
      );
    } else {
      setSelectedExportVariables([...selectedExportVariables, variableId]);
    }
  };
  
  // Select all variables
  const selectAllVariables = () => {
    setSelectedExportVariables(variables.map(v => v.id));
  };
  
  // Clear all variables
  const clearAllVariables = () => {
    setSelectedExportVariables([]);
  };
  
  // Generate share URL and prepare for sharing
  const handleShare = () => {
    // Get the current view mode from URL
    const currentPath = location.pathname;
    let viewMode = 'map';
    
    if (currentPath.includes('analysis')) {
      viewMode = 'analysis';
    } else if (currentPath.includes('comparison')) {
      viewMode = 'comparison';
    } else if (currentPath.includes('advanced-forecast')) {
      viewMode = 'advanced-forecast';
    } else if (currentPath.includes('forecast')) {
      viewMode = 'forecast';
    } else if (currentPath.includes('about')) {
      viewMode = 'about';
    }
    
    // Create state object
    const state = {
      viewMode,
      filters: {
        regions: currentFilters.regions || [],
        variables: currentFilters.variables || [],
        years: currentFilters.years || [],
        countries: currentFilters.countries || []
      },
      variable: selectedVariable,
      year: selectedYear
    };
    
    // Generate share URL
    const url = generateShareUrl(state, shareConfig);
    setShareUrl(url);
    
    // Update social share options with new URL
    setSocialShareOptions({
      ...socialShareOptions,
      url,
      title: `Regional Data Explorer: ${viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} View`,
      description: `Check out this ${viewMode} analysis from the Regional Data Explorer with ${
        currentFilters.regions?.length || 0
      } regions and ${
        currentFilters.variables?.length || 0
      } variables.`
    });
    
    setShareModalOpen(true);
  };
  
  // Copy share URL to clipboard
  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setShareUrlCopied(true);
        setTimeout(() => setShareUrlCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy share URL:', err);
      });
  };
  
  // Handle sharing to a specific platform
  const handleSocialShare = (platform: 'twitter' | 'linkedin' | 'facebook' | 'instagram' | 'email') => {
    shareToPlatform(platform, socialShareOptions);
  };
  
  return (
    <nav
      className="navbar navbar-expand-lg sticky-top"
      style={{
        background: isDarkMode
          ? 'rgba(31, 41, 55, 0.95)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: isDarkMode
          ? '1px solid rgba(75, 85, 99, 0.3)'
          : '1px solid rgba(229, 231, 235, 0.3)',
        boxShadow: isDarkMode
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}
    >
      <div className="container-fluid px-4">
        {/* Brand */}
        <Link
          className="navbar-brand d-flex align-items-center"
          to="/"
          style={{ textDecoration: 'none' }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px'
          }}>
            <i
              className="fas fa-globe-europe"
              style={{
                color: 'white',
                fontSize: '18px'
              }}
            ></i>
          </div>
          <div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              color: isDarkMode ? '#f3f4f6' : '#111827',
              lineHeight: '1.2',
              margin: 0
            }}>Italy Data Explorer</div>
            <div style={{
              fontSize: '12px',
              color: isDarkMode ? '#9ca3af' : '#6b7280',
              fontWeight: '400',
              margin: 0
            }}>Municipal Data Visualization</div>
          </div>
        </Link>
        
        {/* Mobile menu toggle */}
        <button
          className="navbar-toggler border-0"
          type="button"
          onClick={toggleMenu}
          aria-expanded={isMenuOpen ? 'true' : 'false'}
          aria-label="Toggle navigation"
          style={{
            background: isDarkMode
              ? 'rgba(55, 65, 81, 0.5)'
              : 'rgba(249, 250, 251, 0.8)',
            borderRadius: '8px',
            padding: '8px',
            border: `1px solid ${isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.5)'}`,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div className="hamburger">
            <span style={{
              backgroundColor: isDarkMode ? '#e5e7eb' : '#374151',
              display: 'block',
              width: '20px',
              height: '2px',
              margin: '3px 0',
              borderRadius: '1px',
              transition: '0.3s'
            }}></span>
            <span style={{
              backgroundColor: isDarkMode ? '#e5e7eb' : '#374151',
              display: 'block',
              width: '20px',
              height: '2px',
              margin: '3px 0',
              borderRadius: '1px',
              transition: '0.3s'
            }}></span>
            <span style={{
              backgroundColor: isDarkMode ? '#e5e7eb' : '#374151',
              display: 'block',
              width: '20px',
              height: '2px',
              margin: '3px 0',
              borderRadius: '1px',
              transition: '0.3s'
            }}></span>
          </div>
        </button>
        
        <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`}>
          {/* Main Navigation */}
          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/') ? 'active' : ''}`}
                to="/"
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-map-marked-alt me-1"></i>
                <span>Map View</span>
              </Link>
            </li>
            {/* Municipal Data - Temporarily Hidden */}
            {/* <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/municipal') ? 'active' : ''}`}
                to="/municipal"
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-city me-1"></i>
                <span>Municipal Data</span>
              </Link>
            </li> */}
            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/analysis') ? 'active' : ''}`}
                to="/analysis"
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-chart-bar me-1"></i>
                <span>Analysis</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link 
                className={`nav-link ${isActive('/comparison') ? 'active' : ''}`} 
                to="/comparison"
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-balance-scale me-1"></i>
                <span>Comparison</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link 
                className={`nav-link ${isActive('/similarity') ? 'active' : ''}`} 
                to="/similarity"
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-sitemap me-1"></i>
                <span>Similarity</span>
              </Link>
            </li>
            <li className="nav-item dropdown" style={{ position: 'relative' }}>
              <button 
                className={`nav-link btn btn-link ${isActive('/forecast') || isActive('/advanced-forecast') ? 'active' : ''}`} 
                onClick={() => setIsForecastDropdownOpen(!isForecastDropdownOpen)}
                onBlur={(e) => {
                  // Close dropdown when clicking outside, but give time for link clicks
                  const button = e.currentTarget;
                  setTimeout(() => {
                    if (!button.contains(document.activeElement)) {
                      setIsForecastDropdownOpen(false);
                    }
                  }, 150);
                }}
                style={{ 
                  border: 'none', 
                  background: 'none', 
                  padding: '0.5rem 1rem',
                  textDecoration: 'none',
                  position: 'relative'
                }}
              >
                <i className="fas fa-chart-line me-1"></i>
                <span>Forecast</span>
                <i className="fas fa-caret-down ms-1" style={{ fontSize: '0.8em' }}></i>
              </button>
              {isForecastDropdownOpen && (
                <ul 
                  className="dropdown-menu show" 
                  style={{ 
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    zIndex: 1000,
                    display: 'block',
                    minWidth: '200px'
                  }}
                >
                  <li>
                    <Link 
                      className={`dropdown-item ${isActive('/forecast') ? 'active' : ''}`} 
                      to="/forecast"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsForecastDropdownOpen(false);
                      }}
                    >
                      <i className="fas fa-chart-area me-1"></i>
                      Statistical Forecasting
                    </Link>
                  </li>
                  <li>
                    <Link 
                      className={`dropdown-item ${isActive('/advanced-forecast') ? 'active' : ''}`} 
                      to="/advanced-forecast"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsForecastDropdownOpen(false);
                      }}
                    >
                      <i className="fas fa-brain me-1"></i>
                      Advanced Modeling
                    </Link>
                  </li>
                </ul>
              )}
            </li>
            <li className="nav-item">
              <Link 
                className={`nav-link ${isActive('/about') ? 'active' : ''}`} 
                to="/about"
                onClick={() => setIsMenuOpen(false)}
              >
                <i className="fas fa-info-circle me-1"></i>
                <span>About</span>
              </Link>
            </li>
          </ul>
          
          {/* Center Banner - positioned between nav and actions */}
          <div className="navbar-banner d-none d-lg-flex justify-content-center align-items-center">
            <img
              src="/banner_PNRR_1x.webp"
              srcSet="/banner_PNRR_1x.webp 1x, /banner_PNRR_2x.webp 2x"
              alt="PNRR Banner"
              className="navbar-banner-img"
              width="474"
              height="45"
              loading="eager"
              onError={(e) => {
                console.error('Banner image failed to load from:', e.currentTarget.src);
                // Fallback to original PNG if WebP fails
                if (e.currentTarget.src.includes('.webp')) {
                  e.currentTarget.src = '/banner_PNRR.png';
                  e.currentTarget.removeAttribute('srcset');
                } else {
                  e.currentTarget.style.display = 'none';
                }
              }}
            />
          </div>
          
          {/* Right side controls - properly spaced */}
          <div className="navbar-actions d-flex align-items-center">
            {/* Theme Toggle - positioned separately */}
            <div className="theme-toggle me-3">
              <button
                onClick={toggleTheme}
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{
                  background: isDarkMode
                    ? 'rgba(55, 65, 81, 0.8)'
                    : 'rgba(249, 250, 251, 0.8)',
                  border: `1px solid ${isDarkMode ? 'rgba(75, 85, 99, 0.5)' : 'rgba(229, 231, 235, 0.5)'}`,
                  borderRadius: '10px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  color: isDarkMode ? '#e5e7eb' : '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'} me-2`}></i>
                <span className="d-none d-md-inline">
                  {isDarkMode ? 'Light' : 'Dark'}
                </span>
              </button>
            </div>

            {/* Action Buttons - moved to far right */}
            <div className="action-buttons d-flex" style={{ gap: '8px' }}>
              <button
                onClick={() => setExportModalOpen(true)}
                title="Export data"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                <i className="fas fa-download me-2"></i>
                <span className="d-none d-lg-inline">Export</span>
              </button>

              <button
                onClick={handleShare}
                title="Share current view"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                <i className="fas fa-share-alt me-2"></i>
                <span className="d-none d-lg-inline">Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Export Data Modal */}
      {exportModalOpen && (
        <>
          <div className="modal fade show" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1060,
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            overflow: 'hidden'
          }} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg" role="document" style={{
              position: 'relative',
              margin: 0,
              maxWidth: '1000px',
              width: '90%',
              height: '90vh'
            }}>
              <div className="modal-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-header" style={{ flexShrink: 0 }}>
                <h5 className="modal-title">Export Regional Data</h5>
                <button type="button" className="close" onClick={() => setExportModalOpen(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ overflowY: 'auto', flex: '1 1 auto', padding: '1.5rem' }}>
                <div className="form-group">
                  <label>File Format</label>
                  <select
                    className="form-control"
                    value={exportConfig.format}
                    onChange={(e) => setExportConfig({
                      ...exportConfig,
                      format: e.target.value as any
                    })}
                  >
                    <option value="csv">CSV (.csv)</option>
                    <option value="json">JSON (.json)</option>
                    <option value="xlsx">Excel (.xlsx)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>File Name</label>
                  <input 
                    type="text"
                    className="form-control"
                    value={exportConfig.fileName}
                    onChange={(e) => setExportConfig({
                      ...exportConfig,
                      fileName: e.target.value
                    })}
                  />
                </div>
                
                <div className="form-group">
                  <div className="form-check">
                    <input 
                      type="checkbox"
                      className="form-check-input"
                      id="exportFullDataset"
                      checked={exportFullDataset}
                      onChange={(e) => setExportFullDataset(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="exportFullDataset">
                      Export full dataset (ignores current filters)
                    </label>
                    {exportFullDataset && (
                      <small className="form-text text-warning">
                        Warning: The full dataset might be large and could take longer to download.
                      </small>
                    )}
                  </div>
                </div>
                
                <div className="form-check mb-3">
                  <input 
                    type="checkbox"
                    className="form-check-input"
                    id="includeFiltersExport"
                    checked={exportConfig.includeFilters}
                    onChange={(e) => setExportConfig({
                      ...exportConfig,
                      includeFilters: e.target.checked
                    })}
                  />
                  <label className="form-check-label" htmlFor="includeFiltersExport">
                    Include filter metadata in export
                  </label>
                </div>
                
                <div className="form-group">
                  <label>Select Variables to Export</label>
                  <div className="mb-2">
                    <button 
                      className="btn btn-sm btn-outline-secondary mr-2"
                      onClick={selectAllVariables}
                    >
                      Select All
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={clearAllVariables}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="border p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div className="row">
                      {variables.map(variable => (
                        <div className="col-md-4 mb-2" key={variable.id}>
                          <div className="form-check">
                            <input 
                              type="checkbox"
                              className="form-check-input"
                              id={`var-${variable.id}`}
                              checked={selectedExportVariables.includes(variable.id)}
                              onChange={() => handleVariableToggle(variable.id)}
                            />
                            <label className="form-check-label" htmlFor={`var-${variable.id}`}>
                              {variable.label}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <small className="form-text text-muted">
                    {selectedExportVariables.length} of {variables.length} variables selected
                  </small>
                </div>
              </div>
              <div className="modal-footer" style={{ flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setExportModalOpen(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={handleExportData}
                  disabled={selectedExportVariables.length === 0}
                >
                  <i className="fas fa-download mr-1"></i> Export Data
                </button>
              </div>
            </div>
          </div>
          </div>
        </>
      )}
      
      {/* Share Modal */}
      {shareModalOpen && (
        <>
          <div className="modal fade show" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1060,
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            overflow: 'hidden'
          }} tabIndex={-1} role="dialog">
            <div className="modal-dialog" role="document" style={{
              position: 'relative',
              margin: 0,
              maxWidth: '700px',
              width: '90%',
              height: '85vh'
            }}>
              <div className="modal-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-header" style={{ flexShrink: 0 }}>
                <h5 className="modal-title">Share Current View</h5>
                <button type="button" className="close" onClick={() => setShareModalOpen(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ overflowY: 'auto', flex: '1 1 auto', padding: '1.5rem' }}>
                {/* Share URL */}
                <div className="form-group">
                  <label>Share Link</label>
                  <div className="input-group">
                    <input 
                      type="text"
                      className="form-control"
                      value={shareUrl}
                      readOnly
                    />
                    <div className="input-group-append">
                      <button 
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={copyShareUrl}
                      >
                        {shareUrlCopied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  <small className="form-text text-muted">
                    This link will allow others to view the application with your current settings.
                  </small>
                </div>
                
                {/* Social Media Sharing */}
                <div className="mt-4">
                  <label>Share on Social Media</label>
                  <div className="d-flex justify-content-center mt-2">
                    {/* Twitter */}
                    <button 
                      className="btn btn-outline-primary mx-1" 
                      onClick={() => handleSocialShare('twitter')}
                      title="Share on Twitter"
                    >
                      <i className="fab fa-twitter"></i>
                    </button>
                    
                    {/* LinkedIn */}
                    <button 
                      className="btn btn-outline-primary mx-1" 
                      onClick={() => handleSocialShare('linkedin')}
                      title="Share on LinkedIn"
                    >
                      <i className="fab fa-linkedin"></i>
                    </button>
                    
                    {/* Facebook */}
                    <button 
                      className="btn btn-outline-primary mx-1" 
                      onClick={() => handleSocialShare('facebook')}
                      title="Share on Facebook"
                    >
                      <i className="fab fa-facebook"></i>
                    </button>
                    
                    {/* Instagram */}
                    <button 
                      className="btn btn-outline-primary mx-1" 
                      onClick={() => handleSocialShare('instagram')}
                      title="Share on Instagram"
                    >
                      <i className="fab fa-instagram"></i>
                    </button>
                    
                    {/* Email */}
                    <button 
                      className="btn btn-outline-primary mx-1" 
                      onClick={() => handleSocialShare('email')}
                      title="Share via Email"
                    >
                      <i className="fas fa-envelope"></i>
                    </button>
                  </div>
                </div>
                
                {/* Share Configuration Options */}
                <div className="mt-4">
                  <label>Share Options</label>
                  <div className="form-check">
                    <input 
                      type="checkbox"
                      className="form-check-input"
                      id="includeFiltersShare"
                      checked={shareConfig.includeFilters}
                      onChange={(e) => {
                        const newConfig = {
                          ...shareConfig,
                          includeFilters: e.target.checked
                        };
                        setShareConfig(newConfig);
                        updateShareUrl(newConfig);
                      }}
                    />
                    <label className="form-check-label" htmlFor="includeFiltersShare">
                      Include data filters
                    </label>
                  </div>
                  
                  <div className="form-check">
                    <input 
                      type="checkbox"
                      className="form-check-input"
                      id="includeViewConfigShare"
                      checked={shareConfig.includeViewConfiguration}
                      onChange={(e) => {
                        const newConfig = {
                          ...shareConfig,
                          includeViewConfiguration: e.target.checked
                        };
                        setShareConfig(newConfig);
                        updateShareUrl(newConfig);
                      }}
                    />
                    <label className="form-check-label" htmlFor="includeViewConfigShare">
                      Include view configuration
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShareModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
          </div>
        </>
      )}
      
      {/* Modal backdrop */}
      {(exportModalOpen || shareModalOpen) && (
        <div className="modal-backdrop fade show" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1055,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}></div>
      )}
    </nav>
  );
  
  // Helper function to update share URL when configuration changes
  function updateShareUrl(config: ShareConfiguration) {
    // Get the current view mode from URL
    const currentPath = location.pathname;
    let viewMode = 'map';
    
    if (currentPath.includes('analysis')) {
      viewMode = 'analysis';
    } else if (currentPath.includes('comparison')) {
      viewMode = 'comparison';
    } else if (currentPath.includes('advanced-forecast')) {
      viewMode = 'advanced-forecast';
    } else if (currentPath.includes('forecast')) {
      viewMode = 'forecast';
    } else if (currentPath.includes('about')) {
      viewMode = 'about';
    }
    
    // Create state object
    const state = {
      viewMode,
      filters: {
        regions: currentFilters.regions || [],
        variables: currentFilters.variables || [],
        years: currentFilters.years || [],
        countries: currentFilters.countries || []
      },
      variable: selectedVariable,
      year: selectedYear
    };
    
    // Generate share URL
    const url = generateShareUrl(state, config);
    setShareUrl(url);
    
    // Update social share options
    setSocialShareOptions({
      ...socialShareOptions,
      url
    });
  }
};

export default Navbar;
